/**
 * Tiny inline badge icon. Reads from the gamification catalog
 * (key + emoji + color) and renders a colored dot with the emoji.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";

const FALLBACK_CATALOG = {
  first_flight:    { title: "First Flight",    emoji: "🚀", color: "#7ac143", criteria: "First story approved" },
  on_a_roll:       { title: "On a Roll",       emoji: "🔥", color: "#f68b1f", criteria: "Captured a story in 2+ consecutive weeks" },
  generous:        { title: "Generous",        emoji: "💌", color: "#0091be", criteria: "Most kudos given this month" },
  unicorn_finder:  { title: "Unicorn Finder",  emoji: "🦄", color: "#a855f7", criteria: "First story in a brand-new industry" },
  got_receipts:    { title: "Proof in Numbers", emoji: "📈", color: "#083050", criteria: "Story with measurable outcomes filled in" },
  top_of_the_wall: { title: "Top of the Wall", emoji: "🏆", color: "#ffd400", criteria: "Ended a month at #1 on the leaderboard" },
};

export function BadgeIcon({ badgeKey, size = "sm" }) {
  const b = FALLBACK_CATALOG[badgeKey];
  if (!b) return null;
  const dim = size === "lg" ? "w-7 h-7 text-base" : size === "md" ? "w-6 h-6 text-sm" : "w-5 h-5 text-[11px]";
  return (
    <span
      data-testid={`badge-${badgeKey}`}
      title={`${b.title} — ${b.criteria}`}
      className={`inline-flex items-center justify-center rounded-full ring-2 ring-white shadow-sm ${dim}`}
      style={{ background: b.color }}
    >
      {b.emoji}
    </span>
  );
}

/** Row of badges by keys (e.g., user.badges). Skips unknown keys. */
export function BadgeRow({ keys = [], size = "sm", className = "" }) {
  if (!keys || !keys.length) return null;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid="badge-row">
      {keys.map((k, i) => (
        <BadgeIcon key={`${k}-${i}`} badgeKey={k} size={size} />
      ))}
    </span>
  );
}

/** Larger card showing all available badges + which the user owns. */
export function BadgeCard() {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/me/badges").then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => setList([]));
  }, []);
  if (!list.length) return null;
  const earnedCount = list.filter((b) => b.earned).length;
  return (
    <div className="tc-card p-5" data-testid="badge-card">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-lg text-tc-ink">Your badges</h3>
        <span className="tc-pill bg-orange-50 text-orange-800">{earnedCount} / {list.length}</span>
      </div>
      <p className="text-xs text-slate-500 mt-0.5">Hit the criteria once and the badge stays on your record forever.</p>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {list.map((b) => (
          <div
            key={b.key}
            data-testid={`badge-row-${b.key}-${b.earned ? "on" : "off"}`}
            className={`flex items-start gap-2.5 p-3 rounded-xl border ${b.earned ? "border-orange-200 bg-orange-50/30" : "border-slate-200 bg-slate-50 opacity-70"}`}
          >
            <span
              className="w-9 h-9 rounded-full flex items-center justify-center ring-2 ring-white shadow-sm text-base shrink-0"
              style={{ background: b.color }}
            >{b.emoji}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[11px] uppercase tracking-widest font-extrabold text-tc-ink">{b.title}</span>
                {b.earned && <span className="tc-pill bg-green-100 text-green-800 text-[9px]">Earned</span>}
              </div>
              <p className="text-[11px] text-slate-600 leading-snug mt-0.5">{b.criteria}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
